import styled from "styled-components";
import {
    LeftSection,
    SectionHeader,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Card,
} from "../shared/styled";

// ─── Layout ─────────────────────────────────────────────

export const Container = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 20px;
    height: 100%;

    @media (max-width: 1200px) {
        gap: 20px;
        padding: 15px;
    }

    @media (max-width: 992px) {
        grid-template-columns: 1fr;
        gap: 20px;
        padding: 15px;
    }

    @media (max-width: 576px) {
        padding: 10px;
        gap: 15px;
    }
`;

export const CardTitle = styled.h6`
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin: 0 0 15px 0;
`;

export const Toggle = styled.button.attrs<{
    isActive: boolean;
    mainColor: string;
}>(({ isActive }) => ({
    type: "button",
    role: "switch",
    "aria-checked": isActive,
}))<{ isActive: boolean; mainColor: string }>`
    width: 50px;
    height: 26px;
    border-radius: 13px;
    border: none;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#ccc"};
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &::after {
        content: "";
        position: absolute;
        top: 3px;
        left: ${({ isActive }) => (isActive ? "27px" : "3px")};
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background-color: white;
        transition: left 0.2s ease;
    }
`;

// ─── Color Picker ───────────────────────────────────────

export const ColorRow = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: 15px;

    &:last-child {
        margin-bottom: 0;
    }
`;

export const ColorLabel = styled.span`
    font-size: 13px;
    color: #666;
    min-width: 140px;
`;

export const ColorSwatch = styled.div<{ color: string }>`
    width: 36px;
    height: 36px;
    border-radius: 6px;
    background-color: ${({ color }) => color};
    border: 2px solid #e0e0e0;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: scale(1.1);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }
`;

export const ColorValue = styled.span`
    font-size: 12px;
    color: #999;
    font-family: monospace;
`;

export const PopoverContainer = styled.div`
    position: absolute;
    z-index: 100;
`;

export const PopoverCover = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
`;

export const ResetButton = styled.button<{ mainColor: string }>`
    padding: 6px 14px;
    background-color: transparent;
    color: ${({ mainColor }) => mainColor};
    border: 1px solid ${({ mainColor }) => mainColor};
    border-radius: 6px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        background-color: ${({ mainColor }) => mainColor};
        color: white;
    }
`;

// ─── Restaurant Info ────────────────────────────────────

export const LanguageTabs = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 16px;
`;

export const LanguageTab = styled.button<{
    isActive: boolean;
    mainColor: string;
}>`
    padding: 5px 14px;
    border-radius: 20px;
    border: 1px solid
        ${({ isActive, mainColor }) => (isActive ? mainColor : "#d0d0d0")};
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#fff"};
    color: ${({ isActive }) => (isActive ? "#fff" : "#555")};
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${({ mainColor }) => mainColor};
    }
`;

export const ColumnHeaders = styled.div`
    display: grid;
    grid-template-columns: 80px 1fr 1fr auto;
    gap: 10px;
    align-items: center;
    margin-bottom: 10px;
    padding: 0 4px;
`;

export const ColumnHeaderLabel = styled.span`
    font-size: 12px;
    color: #999;
    font-weight: 500;
`;

export const InfoRow = styled.div`
    display: grid;
    grid-template-columns: 80px 1fr 1fr auto;
    gap: 10px;
    align-items: center;
    margin-bottom: 12px;

    &:last-child {
        margin-bottom: 0;
    }
`;

export const InfoRowLabel = styled.span`
    font-size: 13px;
    color: #555;
    font-weight: 500;
`;

export const InfoInput = styled.input`
    padding: 8px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    font-size: 13px;
    color: #333;
    background-color: #fff;
    width: 100%;
    min-width: 0;
    transition: border-color 0.2s ease;

    &:focus {
        outline: none;
        border-color: #999;
    }

    &:disabled {
        background-color: #f5f5f5;
        color: #999;
        cursor: not-allowed;
    }
`;

export const InfoActions = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

export const EyeButton = styled.button<{
    isVisible: boolean;
    mainColor: string;
}>`
    width: 30px;
    height: 30px;
    border-radius: 6px;
    border: none;
    background-color: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all 0.2s ease;

    &:hover {
        background-color: ${({ mainColor }) => `${mainColor}15`};
    }

    svg {
        width: 18px;
        height: 18px;
        fill: ${({ isVisible, mainColor }) => (isVisible ? mainColor : "#ccc")};
    }
`;

export const MiniToggle = styled.button<{
    isActive: boolean;
    mainColor: string;
}>`
    width: 36px;
    height: 20px;
    border-radius: 10px;
    border: none;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#ccc"};
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease;
    flex-shrink: 0;

    &::after {
        content: "";
        position: absolute;
        top: 2px;
        left: ${({ isActive }) => (isActive ? "18px" : "2px")};
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background-color: white;
        transition: left 0.2s ease;
    }
`;

export const ResetIconButton = styled.button<{ mainColor: string }>`
    width: 30px;
    height: 30px;
    border-radius: 6px;
    border: none;
    background-color: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all 0.2s ease;

    &:hover {
        background-color: ${({ mainColor }) => `${mainColor}15`};
    }

    svg {
        width: 16px;
        height: 16px;
        fill: ${({ mainColor }) => mainColor};
    }
`;

// ─── Social Networks ────────────────────────────────────

export const SocialItem = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
        border-bottom: none;
    }
`;

export const SocialIconBox = styled.div<{ enabled: boolean }>`
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background-color: ${({ enabled }) => (enabled ? "#333" : "#e0e0e0")};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background-color 0.2s ease;

    svg {
        width: 16px;
        height: 16px;
        fill: white;
    }
`;

export const SocialInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const SocialName = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: #333;
`;

export const SocialUrlInput = styled.input`
    padding: 5px 8px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-size: 11px;
    color: #666;
    width: 100%;

    &:focus {
        outline: none;
        border-color: #999;
    }

    &:disabled {
        background-color: #f5f5f5;
        cursor: not-allowed;
    }
`;

export const SocialToggle = styled.button<{
    isActive: boolean;
    mainColor: string;
}>`
    width: 40px;
    height: 22px;
    border-radius: 11px;
    border: none;
    background-color: ${({ isActive, mainColor }) =>
        isActive ? mainColor : "#ccc"};
    position: relative;
    cursor: pointer;
    transition: background-color 0.2s ease;
    flex-shrink: 0;

    &::after {
        content: "";
        position: absolute;
        top: 2px;
        left: ${({ isActive }) => (isActive ? "20px" : "2px")};
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background-color: white;
        transition: left 0.2s ease;
    }
`;

// ─── Language Settings ──────────────────────────────────

export const LanguageItem = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
        border-bottom: none;
    }
`;

export const LanguageCodeBadge = styled.div<{
    enabled: boolean;
    mainColor: string;
}>`
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background-color: ${({ enabled, mainColor }) =>
        enabled ? mainColor : "#e0e0e0"};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background-color 0.2s ease;
    font-size: 11px;
    font-weight: 700;
    color: white;
`;

export const LanguageLabelText = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: #333;
    flex: 1;
`;

export const DefaultLanguageBadge = styled.button<{
    isDefault: boolean;
    mainColor: string;
}>`
    padding: 4px 10px;
    border-radius: 12px;
    border: 1px solid
        ${({ isDefault, mainColor }) => (isDefault ? mainColor : "#d0d0d0")};
    background-color: ${({ isDefault, mainColor }) =>
        isDefault ? mainColor : "transparent"};
    color: ${({ isDefault }) => (isDefault ? "#fff" : "#888")};
    font-size: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        border-color: ${({ mainColor }) => mainColor};
        color: ${({ isDefault, mainColor }) =>
            isDefault ? "#fff" : mainColor};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
`;

// Shared layout primitives (see ../shared/styled).
export {
    LeftSection,
    SectionHeader,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Card,
};
